import { Component } from "vue-property-decorator";
import {
  ConsoleLogger,
  DefaultActiveSpeakerPolicy,
  DefaultDeviceController,
  DefaultMeetingSession,
  DefaultModality,
  DefaultVideoTransformDevice,
  LogLevel,
  MeetingSessionConfiguration,
  BackgroundReplacementVideoFrameProcessor,
  BackgroundBlurVideoFrameProcessor,
  BackgroundBlurOptions,
  VoiceFocusDeviceTransformer,
  VoiceFocusSpec,
  DefaultEventController,
  // VideoAdaptiveProbePolicy,
  SimulcastLayers,
  VoiceFocusTransformDevice,
  // DefaultBrowserBehavior,
} from "amazon-chime-sdk-js";
import { i18n } from "@/resources/locales";
import { mixins } from "vue-class-component";
import UserValidator from "@/resources/mixins/userValidator";
import { MEETING_TYPE } from "@/resources/constants/meeting";
import Loader from "@/resources/mixins/loader/loader";
import { ConfigInterface } from "@/resources/constants/system";
import { store } from "@/store";
@Component({})
export default class ChimeComponent extends mixins(UserValidator, Loader) {
  QUALITY_LOW = {
    width: '640',
    height: '360',
    frameRate: '15',
    kbps: 600,
    shareFrameRate: 10,
  };
  QUALITY_MEDIUM = {
    width: '960',
    height: '540',
    frameRate: '15',
    kbps: 1000,
    shareFrameRate: 10,
  };
  QUALITY_HIGH = {
    width: '1280',
    height: '720',
    frameRate: '15',
    kbps: 1400,
    shareFrameRate: 10,
  };
  CURRENT_QUALITY: any;
  soundUser = [];
  muteNotification = null;
  stopNotification = null;
  micNotification = null;
  camNotification = null;
  buzzedNotification = null;
  activeSystem = true;
  audio = new Audio(require("@/assets/audio/user_joinedroom.mp3"));
  isPlay = false;
  oldSendBandwidthKbs = 0;
  oldRecvBandwidthKbs = 0;
  constructor() {
    super();
    this.CURRENT_QUALITY = this.QUALITY_LOW;
  }
  /**
   * Steep 1
   * Initial call, create meeting Object
   * */
  async createMeeting(): Promise<void> {
    const meeting = this.$store.getters["MeetingStore/getMeeting"];
    const attendee = this.$store.getters["MeetingStore/getAttendee"];
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    const logger = new ConsoleLogger(
      "CHIME_CALL_LOGS createMeeting ",
      process.env.VUE_APP_ENVIRONMENT === "PRO" ? LogLevel.OFF : LogLevel.ERROR
    );
    const deviceController = new DefaultDeviceController(logger, {
      enableWebAudio: await VoiceFocusDeviceTransformer.isSupported(),
    });
    const meetingConfig = new MeetingSessionConfiguration(meeting, attendee);
    // const browserBehavior = new DefaultBrowserBehavior();
    // if (browserBehavior.isSimulcastSupported()) {
    //   meetingConfig.enableSimulcastForUnifiedPlanChromiumBasedBrowsers = true;
    //   meetingConfig.videoDownlinkBandwidthPolicy = new VideoAdaptiveProbePolicy(logger);
    // }
    const eventController = new DefaultEventController(meetingConfig, logger)
    let meetingSession = new DefaultMeetingSession(
      meetingConfig,
      logger,
      deviceController,
      eventController
    );
    await this.$store.dispatch(
      "MeetingStore/setMeetingSessionAction",
      meetingSession
    );
    meetingSession.audioVideo.chooseVideoInputQuality(this.CURRENT_QUALITY.width, this.CURRENT_QUALITY.height, this.CURRENT_QUALITY.frameRate);
    meetingSession.audioVideo.setVideoMaxBandwidthKbps(this.CURRENT_QUALITY.kbps);

    await this.setMeetingAudioInputDevice(meetingSession);
    const config: ConfigInterface = await this.$store.getters[
      "UserStore/getConfig"
    ];

    //if (!config.call.noCam) {
    await this.setMeetingVideoInputDevice(meetingSession, this.$store);
    //}

    if (meetingType === MEETING_TYPE.WEBINAR) {
      await this.$store.dispatch("MeetingStore/setMicEnabledAction", false);
      await this.$store.dispatch("MeetingStore/setCameraEnabledAction", false);
    }

    await this.bindMeetingAudioOutput(meetingSession);
    await this.bindMeetingLocalVideo(meetingSession);

    if (meetingType !== MEETING_TYPE.WEBINAR) {
      await this.$store.dispatch(
        "MeetingStore/setCameraEnabledAction",
        !config.call.noCam
      );
      await this.$store.dispatch(
        "MeetingStore/setMicEnabledAction",
        !config.call.noMic
      );
    }

    await this.removeOrAddAttendee(meetingSession);
    setTimeout(async () => {
      await this.subscribeToAttendeeVolumeChange(meetingSession);
    }, 300);

    const micEnabled = this.$store.getters["MeetingStore/getMicEnabled"];
    await this.muteOrUnmuteAudioInput(micEnabled);

    const camEnabled = this.$store.getters["MeetingStore/getCameraEnabled"];
    await this.startOrStopLocalVideo(camEnabled);

    this.connectionsMetrics(meetingSession);

    eventController.addObserver({
      eventDidReceive: (name, attributes) => {
        switch (name) {
          case 'meetingStartFailed':
            if (process.env.VUE_APP_ENVIRONMENT != "PRO")
              console.log("CHIME OBSERVER MEETING ", name);
            this.closeDevices(meetingSession);
            this.stopLoader(this.$store);
            store.dispatch("SystemStore/setShowCallErrorRetryModalAction", true);
            store.dispatch("SystemStore/setEducationModalAction", false);
            /*store.dispatch("LogsStore/sendGraphicClientErrorAction", {
              data: {
                type: "WEB_CLIENT",
                errorMessage: name,
                userMessage: ""
              },
              callback: () => { },
              callbackError: () => { },
            });*/
            break;
          case 'meetingFailed':
            if (process.env.VUE_APP_ENVIRONMENT != "PRO")
              console.log("CHIME OBSERVER MEETING ", name);
            this.closeDevices(meetingSession);
            this.stopLoader(this.$store);
            store.dispatch("SystemStore/setShowCallErrorRetryModalAction", true);
            store.dispatch("SystemStore/setEducationModalAction", false);
            /*store.dispatch("LogsStore/sendGraphicClientErrorAction", {
              data: {
                type: "WEB_CLIENT",
                errorMessage: name,
                userMessage: ""
              },
              callback: () => { },
              callbackError: () => { },
            });*/
            break;
          case 'meetingEnded':
            if (process.env.VUE_APP_ENVIRONMENT != "PRO")
              console.log("CHIME OBSERVER MEETING ", name);
            meetingSession.audioVideo.stopAudioInput();
            meetingSession.audioVideo.stopLocalVideoTile();
            meetingSession.audioVideo.realtimeMuteLocalAudio();
            meetingSession.audioVideo.stop();
            meetingSession.audioVideo.stopContentShare();
            this.$store.dispatch("UserStore/loadPreviousStatusAction", {
              store: this.$store,
              callback: (response) => {
                this.$store.dispatch(
                  "SpaceStore/updateUserInSpace",
                  response.data
                );
              },
              callbackError: (error) => {
                console.log("CHIME loadPreviousStatusAction ", error);
              },
            });
            this.$store.dispatch("SidebarStore/setOpenCall", false);
            this.$store.dispatch("ChatStore/setMinimizeChatAction", false);
            this.$store.dispatch("ChatStore/setUserChatAction", null);
            setTimeout(() => {
              this.$store.dispatch(
                "GraphicClientStore/resizeGraphicClientAction"
              );
            }, 20);
            this.closeDevices(meetingSession);
            break;
          case 'meetingStartSucceeded':
            if (process.env.VUE_APP_ENVIRONMENT != "PRO")
              console.log("CHIME OBSERVER MEETING ", name);
            this.stopLoader(this.$store);
            break;
          default:
            if (process.env.VUE_APP_ENVIRONMENT != "PRO")
              console.log("CHIME OBSERVER MEETING DEFAULT ", name);
            break;
        }
      }
    });

    const SimulcastLayersMapping = {
      [SimulcastLayers.Low]: 'Low',
      [SimulcastLayers.LowAndMedium]: 'Low and Medium',
      [SimulcastLayers.LowAndHigh]: 'Low and High',
      [SimulcastLayers.Medium]: 'Medium',
      [SimulcastLayers.MediumAndHigh]: 'Medium and High',
      [SimulcastLayers.High]: 'High'
    };


    const observer = {
      encodingSimulcastLayersDidChange: simulcastLayers => {
        if (process.env.VUE_APP_ENVIRONMENT != "PRO")
          console.log(`CHIME current active simulcast layers changed to: ${SimulcastLayersMapping[simulcastLayers]}`);
      },
      voiceFocusFellBackToInnerStream: (device: VoiceFocusTransformDevice, error: Error) => {
        console.log("CHIME voiceFocusFellBackToInnerStream DEVICE ", device, " ERROR ", error);
      },
      voiceFocusInsufficientResources: () => {
        console.log("CHIME voiceFocusInsufficientResources ");
      }
    }

    meetingSession.audioVideo.addObserver(observer);

    await this.receiveDataMessage();
    this.onDisplayModeChange();
  }
  closeDevices(meetingSession: DefaultMeetingSession) {
    meetingSession.audioVideo.stopVideoInput();
    meetingSession.audioVideo.stopAudioInput();
    meetingSession.deviceController.destroy();
  }
  /**
   * Step 2
   * Set audio input
   * */
  async setMeetingAudioInputDevice(
    meetingSession: DefaultMeetingSession,
    store = null
  ): Promise<void> {
    const isFirefox = /^((?!chrome|android).)*firefox/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const audioInputId = !store
      ? this.$store.getters["MeetingStore/getSelectedAudioInput"]
      : store?.getters["MeetingStore/getSelectedAudioInput"];
    const microphone = "microphone" as PermissionName;

    if (navigator != undefined && navigator.permissions != undefined) {
      if (!isFirefox && !isSafari) {
        const microphonePermission = await navigator.permissions
          .query({
            name: microphone,
          })
          .then((permissionStatus) => {
            permissionStatus.addEventListener("change", async () => {
              await this.setMeetingAudioInputDevice(
                meetingSession,
                !store ? this.$store : store
              );
            });
            return permissionStatus;
          });
        if (
          microphonePermission.state === "prompt" ||
          microphonePermission.state === "denied"
        ) {
          console.error("NO AUDIO INPUT PERMISSION");
          await this.stopLoader(!store ? this.$store : store);
        }
      }
    }
    if (isFirefox || isSafari) {
      navigator.mediaDevices
        .getUserMedia({ video: false, audio: true })
        .then(async () => {
        })
        .catch(async () => {
          console.error("NO AUDIO INPUT PERMISSION");
          await this.stopLoader(!store ? this.$store : store);
        });
    }

    const audioInputDevices =
      await meetingSession?.audioVideo.listAudioInputDevices();
    if (!audioInputDevices || audioInputDevices.length === 0) {
      console.error("NO AUDIO INPUT");
      await this.stopLoader(!store ? this.$store : store);
    }
    try {
      const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
      const space = this.$store.getters["SpaceStore/getSpace"];
      if (
        meetingType === MEETING_TYPE.WEBINAR &&
        (space.superSpace.isMultiWebinarActivated
          ? !this.isMultiWebinarUser()
          : !this.isWebinarUser())
      ) {
        meetingSession?.audioVideo.startAudioInput(null);
      } else {
        await meetingSession?.audioVideo.startAudioInput(
          this.checkDeviceIsAvailable(audioInputId, audioInputDevices)
            ? audioInputId
            : audioInputDevices[0].deviceId
        );
      }
    } catch (error) {
      switch (error["name"]) {
        case "NotReadableError":
        case "TypeError":
        case "NotAllowedError":
        case "PermissionDeniedError":
        case "SecurityError": {
          const permision = await this.checkPermisionMicro();
          if (permision) {
            if (this.micNotification) this.micNotification.close();
            this.micNotification = this.$vs.notification({
              sticky: true,
              buttonClose: true,
              duration: "none",
              clickClose: true,
              position: "bottom-center",
              color: "danger",
              title: i18n.t("notifications.devices.title"),
              text: i18n.t("notifications.devices.noPermissions", {
                devices: i18n.t("notifications.devices.mic"),
              }),
            });
            if (localStorage.getItem("OSName") == "Mac/iOS") {
              if (process.env.VUE_APP_ENVIRONMENT != "PRO")
                console.log("CHIME System permission denied audio");
              if (this.activeSystem)
                window.open(
                  "x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone"
                );
              this.activeSystem = false;
              setTimeout(() => {
                this.activeSystem = true;
              }, 2000);
            }
          }
          break;
        }
      }
    }

    await this.$store.dispatch(
      "MeetingStore/setVoiceIsSupportedAction",
      await VoiceFocusDeviceTransformer.isSupported()
    );
    const deviceType = localStorage.getItem("deviceType");
    const voiceIsEnabled =
      this.$store.getters["MeetingStore/getVoiceIsEnabled"];
    if (voiceIsEnabled && deviceType !== "TABLET") {
      setTimeout(async () => {
        await this.addVoiceFocus();
      }, 1000);
    }

    /*if (!audioInput) {
      await this.stopLoader(!store ? this.$store : store);
    }*/
  }

  /**
   * Step 3
   * Set audio output device
   * */
  async setMeetingAudioOutputDevice(meetingSession, store): Promise<void> {
    const audioOutputId = !store
      ? this.$store.getters["MeetingStore/getSelectedAudioOutput"]
      : store?.getters["MeetingStore/getSelectedAudioOutput"];
    const audioOutputDevices =
      await meetingSession?.audioVideo.listAudioOutputDevices();
    if (!audioOutputDevices || audioOutputDevices.length === 0) return;
    try {
      await meetingSession?.audioVideo.startAudioInput(
        this.checkDeviceIsAvailable(audioOutputId, audioOutputDevices)
          ? audioOutputId
          : audioOutputDevices[0].deviceId
      );
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * Step 4
   * Bind output audio
   * */
  private async bindMeetingAudioOutput(meetingSession) {
    const audioElement = document.getElementById("my-audio-element");
    if (!(audioElement instanceof HTMLAudioElement)) return;
    await this.setMeetingAudioOutputDevice(meetingSession, this.$store);
    const config: ConfigInterface = await this.$store.getters[
      "UserStore/getConfig"
    ];
    audioElement.volume = config.call.sound;
    meetingSession?.audioVideo.bindAudioElement(audioElement);
    const observer = {
      audioVideoDidStart: async () => {
        const preStatus = JSON.parse(localStorage.getItem("pre_status"));
        const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
        if (
          preStatus.availableStatus === "GREEN" &&
          meetingType === MEETING_TYPE.ONE_TO_ONE
        ) {
          await this.$store.dispatch(
            "MeetingStore/setCameraEnabledAction",
            false
          );
          await this.$store.dispatch("MeetingStore/setMicEnabledAction", false);
          await this.muteOrUnmuteAudioInput(false);
          await this.startOrStopLocalVideo(false);
        }
      },
    };
    meetingSession?.audioVideo.addObserver(observer);
  }

  async updateBindAudioOutput(meetingSession, store) {
    const audioElement = document.getElementById("my-audio-element");
    if (!(audioElement instanceof HTMLAudioElement)) return;
    await this.setMeetingAudioOutputDevice(meetingSession, store);
    const config: ConfigInterface = await this.$store.getters[
      "UserStore/getConfig"
    ];
    audioElement.volume = config.call.sound;
    meetingSession?.audioVideo.bindAudioElement(audioElement);
  }

  /**
   * Step 5
   * Set video devices
   * */
  async setMeetingVideoInputDevice(
    meetingSession,
    store = null
  ): Promise<void> {
    const isFirefox = /^((?!chrome|android).)*firefox/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const videoInputId = !store
      ? this.$store.getters["MeetingStore/getSelectedVideoInput"]
      : store?.getters["MeetingStore/getSelectedVideoInput"];
    const cam = "camera" as PermissionName;
    if (navigator.permissions != undefined) {
      if (!isFirefox && !isSafari) {
        const cameraPermission = await navigator.permissions
          .query({
            name: cam,
          })
          .then((permissionStatus) => {
            permissionStatus.addEventListener("change", async () => {
              await this.setMeetingVideoInputDevice(
                meetingSession,
                !store ? this.$store : store
              );
            });
            return permissionStatus;
          });
        if (
          cameraPermission.state === "prompt" ||
          cameraPermission.state === "denied"
        ) {
          console.error("NO VIDEO INPUT PERMISSION");
          await this.stopLoader(!store ? this.$store : store);
        }
      }
      if (isFirefox || isSafari) {
        navigator.mediaDevices
          .getUserMedia({ video: true, audio: false })
          .then(async () => {
          })
          .catch(async () => {
            console.error("NO VIDEO INPUT PERMISSION");
            await this.stopLoader(!store ? this.$store : store);
          });
      }
    }

    const videoInputDevices =
      await meetingSession?.audioVideo.listVideoInputDevices(true);
    if (!videoInputDevices || videoInputDevices.length === 0) {
      console.error("NO VIDEO INPUT");
      await this.stopLoader(!store ? this.$store : store);
    }

    try {
      const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
      const space = this.$store.getters["SpaceStore/getSpace"];
      if (
        meetingType === MEETING_TYPE.WEBINAR &&
        (space.superSpace.isMultiWebinarActivated
          ? !this.isMultiWebinarUser()
          : !this.isWebinarUser())
      ) {
        await meetingSession.audioVideo.stopLocalVideoTile();
      } else {
        await meetingSession?.audioVideo.startVideoInput(
          this.checkDeviceIsAvailable(videoInputId, videoInputDevices)
            ? videoInputId
            : videoInputDevices[0].deviceId
        );
      }
    } catch (error) {
      switch (error["name"]) {
        case "NotReadableError":
        case "TypeError":
        case "NotAllowedError":
        case "PermissionDeniedError":
        case "SecurityError": {
          const videoDevices = [];
          const deviceList = await navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => devices);
          const mediaDevicesList = JSON.parse(JSON.stringify(deviceList));
          mediaDevicesList.forEach((dev) => {
            if (dev.kind === "videoinput") videoDevices.push(dev);
          });

          if (this.camNotification) this.camNotification.close();
          const permision = await this.checkPermisionCamera();
          if (permision && videoDevices.length > 0) {
            this.camNotification = this.$vs.notification({
              sticky: true,
              buttonClose: true,
              duration: "none",
              clickClose: true,
              position: "bottom-center",
              color: "danger",
              title: i18n.t("notifications.devices.title"),
              text: i18n.t("notifications.devices.noPermissions", {
                devices: i18n.t("notifications.devices.cam"),
              }),
            });
            if (localStorage.getItem("OSName") == "Mac/iOS") {
              if (this.activeSystem)
                window.open(
                  "x-apple.systempreferences:com.apple.preference.security?Privacy_Camera"
                );
              this.activeSystem = false;
              setTimeout(() => {
                this.activeSystem = true;
              }, 2000);
            }
          } else if (videoDevices.length === 0) {
            this.camNotification = this.$vs.notification({
              sticky: true,
              buttonClose: true,
              duration: "none",
              clickClose: true,
              position: "bottom-center",
              color: "danger",
              title: i18n.t("notifications.devices.title"),
              text: i18n.t("notifications.devices.withoutDevices", {
                devices: i18n.t("notifications.devices.cam"),
              }),
            });
          }
          break;
        }
      }
    }
    const blurEnabled = this.$store.getters["MeetingStore/getBlurEnabled"];
    const backgroundEnabled =
      this.$store.getters["MeetingStore/getBackgroundEnabled"];

    await this.$store.dispatch(
      "MeetingStore/setBlurIsSupportedAction",
      await BackgroundBlurVideoFrameProcessor.isSupported()
    );
    await this.$store.dispatch(
      "MeetingStore/setBackgroundIsSupportedAction",
      await BackgroundReplacementVideoFrameProcessor.isSupported()
    );
    if (blurEnabled && videoInputDevices?.length > 0) {
      const blurAmount = this.$store.getters["MeetingStore/getBlurAmount"];
      await this.addBackgroundBlur(blurAmount);
      /*setTimeout(async () => {
        const blurAmount = this.$store.getters["MeetingStore/getBlurAmount"];
        await this.addBackgroundBlur(blurAmount);
      }, 2000);*/
    } else if (backgroundEnabled && videoInputDevices?.length > 0) {
      const backgroundImage =
        this.$store.getters["MeetingStore/getBackgroundImage"];
      await this.addBackgroundImage(backgroundImage);
      /*setTimeout(async () => {
        await this.addBackgroundImage(backgroundImage);
      }, 2000);*/
    }

    /*if (!videoInput) {
      await this.stopLoader(!store ? this.$store : store);
    }*/
  }
  /**
   * Step 6
   * Bind video local and external video
   * */
  private async bindMeetingLocalVideo(meetingSession): Promise<void> {
    const videoElement = document.getElementById("my-video-element");
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];

    const observer = {
      audioVideoDidStop: async (sessionStatus) => {
        if (process.env.VUE_APP_ENVIRONMENT !== "PRO")
          console.log(
            "CHIME bindMeetingLocalVideo",
            sessionStatus.statusCode()
          );
        meetingSession.deviceController.destroy();
        if (this.muteNotification) this.muteNotification.close();
        if (this.stopNotification) this.stopNotification.close();
        this.muteNotification = null;
        this.stopNotification = null;
      },
      videoTileWasRemoved: async (tile) => {
        if (process.env.VUE_APP_ENVIRONMENT !== "PRO")
          console.log("CHIME videoTileWasRemoved ", tile);
      },
      videoTileDidUpdate: async (tileState) => {
        const { boundAttendeeId, localTile, tileId } = tileState;
        if (!boundAttendeeId) {
          return;
        }
        if (localTile) {
          const space = this.$store.getters["SpaceStore/getSpace"];
          if (
            meetingType === MEETING_TYPE.WEBINAR &&
            (space.superSpace.isMultiWebinarActivated
              ? !this.isMultiWebinarUser()
              : !this.isWebinarUser())
          ) {
            const localParticipant =
              document.getElementById("local-participant");
            if (localParticipant) localParticipant.style.display = "none";
            await this.stopLoader(this.$store);
          } else {
            if (!(videoElement instanceof HTMLVideoElement)) {
              await this.stopLoader(this.$store);
              return;
            }
            //await meetingSession.audioVideo.chooseVideoInputQuality(this.CURRENT_QUALITY.width, this.CURRENT_QUALITY.height, this.CURRENT_QUALITY.frameRate);
            //await meetingSession.audioVideo.setVideoMaxBandwidthKbps(this.CURRENT_QUALITY.kbps);
            this.displayLocalName();
            meetingSession?.audioVideo.bindVideoElement(
              tileState.tileId,
              videoElement
            );
            this.expandVideoOnClick(`local-participant`);
            const isFirstTime =
              this.$store.getters["MeetingStore/getIsFirstConnection"];
            if (!isFirstTime && tileState.active) {
              //await this.sendDataMessage("user_connected", boundAttendeeId);
              await this.$store.dispatch(
                "MeetingStore/setIsFirstConnectionAction",
                true
              );
            }
            setTimeout(() => {
              this.stopLoader(this.$store);
            }, 2000);
          }
          return;
        } else {
          const space = this.$store.getters["SpaceStore/getSpace"];
          if (
            (meetingType === MEETING_TYPE.WEBINAR &&
              !space.superSpace.isMultiWebinarActivated) ||
            meetingType !== MEETING_TYPE.WEBINAR
          )
            this.addUsersInCall(tileState, boundAttendeeId);
          const splitData = tileState.boundExternalUserId.split("#");
          const indexWebinarHost = splitData.findIndex((a) =>
            a.includes("isWebinarHost")
          );
          const isWebinarHost =
            indexWebinarHost >= 0
              ? splitData[indexWebinarHost].split("-")[1] === "1"
              : false;
          if (
            (meetingType === MEETING_TYPE.WEBINAR && isWebinarHost) ||
            meetingType !== MEETING_TYPE.WEBINAR
          ) {
            const user = this.searchAttendeeUser(tileState);
            await this.addOtherMeetingUser(
              meetingSession,
              tileState,
              boundAttendeeId,
              this.searchAttendeeName(tileState),
              user && user.user.profileImage
                ? user.user.profileImage.url
                : // eslint-disable-next-line @typescript-eslint/no-var-requires
                require("@/assets/images/logo/favicon.png")
            );

            this.expandVideoOnClick(
              `chime-participant-${tileState.boundAttendeeId}`
            );
            /* this.simulateClickInShareScreen(
              `chime-participant-${boundAttendeeId}`
            ); */
          }
        }

        this.onDisplayModeChange();
      },
      contentShareDidStart: () => {
        this.$store.dispatch("MeetingStore/setSharedScreenEnabledAction", true);
      },
      contentShareDidStop: () => {
        this.$store.dispatch(
          "MeetingStore/setSharedScreenEnabledAction",
          false
        );
      },
      // remoteVideoSourcesDidChange(videoSources: VideoSource[]) {},
    };

    await meetingSession?.audioVideo.addContentShareObserver(observer);
    await meetingSession?.audioVideo.addObserver(observer);

    await meetingSession?.audioVideo.start();

    if (meetingType === MEETING_TYPE.WEBINAR)
      await meetingSession?.audioVideo.stopLocalVideoTile();
  }

  private async addOtherMeetingUser(
    meetingSession,
    tileState,
    attendeeId,
    externalUserId,
    image
  ) {
    const userId = parseInt(
      tileState.boundExternalUserId.split("#")[0].split("-")[1]
    );
    const renderVideo = document.getElementById(
      `chime-participant-${attendeeId}`
    );

    /* If the video element exist*/
    if (renderVideo) {
      const videoElement = document.getElementById(`video-${attendeeId}`);
      meetingSession.audioVideo.bindVideoElement(
        tileState.tileId,
        videoElement
      );
      return;
    }
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    const user = this.$store.getters["UserStore/getSpaceUser"];
    /* Else, create new video element */
    const meetingParticipants = document.getElementById("meeting-participants");
    const newParticipantContainer = document.createElement("div");
    const newVideoContainer = document.createElement("video");
    const newSpanName = document.createElement("span");
    const newFullScreenIcon = document.createElement("i");
    /*  const infoIcon = document.createElement("i"); */
    let videoActionsContainer = null;
    let muteIcon = null;
    let stopVideoIcon = null;
    let buzzedIcon = null;
    if (!tileState.boundAttendeeId.includes("#content")) {
      videoActionsContainer = document.createElement("div");
      muteIcon = document.createElement("i");
      stopVideoIcon = document.createElement("i");
      buzzedIcon = document.createElement("i") as HTMLButtonElement;
    }
    /* set the label name*/
    newSpanName.setAttribute("id", `span-${attendeeId}`);
    newSpanName.setAttribute("class", "title-attendee");
    newSpanName.innerText = externalUserId !== undefined ? externalUserId : "";

    newFullScreenIcon.setAttribute("class", "expand fas fa-expand");
    newFullScreenIcon.onclick = () => {
      newVideoContainer.requestFullscreen();
    };

    /* infoIcon.setAttribute("class", "info fas fa-info-circle");
    infoIcon.onclick = () => {
      this.$store.dispatch("SystemStore/setUserInfoIdAction", userId);
      this.$store.dispatch("SystemStore/setOpenUserInfoModalAction", true);
    }; */
    if (videoActionsContainer)
      videoActionsContainer.setAttribute("class", "video-actions-container");
    // Video
    newVideoContainer.setAttribute("autoplay", "true");
    newVideoContainer.setAttribute("muted", "true");
    newVideoContainer.setAttribute("playsinline", "true");
    newVideoContainer.setAttribute("id", `video-${attendeeId}`);
    newVideoContainer.setAttribute("class", "video-element");
    newVideoContainer.setAttribute("class", "video-element");
    newVideoContainer.setAttribute("poster", image);
    newParticipantContainer.setAttribute(
      "id",
      `chime-participant-${attendeeId}`
    );
    newParticipantContainer.setAttribute("class", "video-container");
    // newParticipantContainer.style.marginBottom = '40px';
    newParticipantContainer.appendChild(newVideoContainer);
    newParticipantContainer.appendChild(newSpanName);
    newParticipantContainer.appendChild(newFullScreenIcon);
    const space = this.$store.getters["SpaceStore/getSpace"];
    const isMultiWebinar = space.superSpace.isMultiWebinarActivated;

    if (
      (meetingType === MEETING_TYPE.WEBINAR &&
        (
          (isMultiWebinar && (this.isMultiWebinarUser() || this.isSuperSpaceAdmin())) ||
          (!isMultiWebinar && (this.isAdmin() || this.isWebinarUser()))
        )
      ) ||
      (meetingType === MEETING_TYPE.MEETING &&
        (this.isAdmin() || this.isSuperSpaceAdmin() || space.canUsersMuteMicrophoneOrTurnOffCamera || this.canManageMeetingControl())) ||
      (meetingType !== MEETING_TYPE.MEETING && meetingType !== MEETING_TYPE.WEBINAR)
    ) {
      if (videoActionsContainer) {
        muteIcon.setAttribute("class", "external-mute fas fa-volume-mute tooltip");
        const muteTooltip = document.createElement("span");
        muteTooltip.setAttribute("class", "tooltiptext");
        muteTooltip.innerText = "" + i18n.t("tooltips.mute");
        muteIcon.onclick = () => {
          this.sendDataMessage("stop_audio", attendeeId);
        };
        muteIcon.appendChild(muteTooltip);
        stopVideoIcon.setAttribute(
          "class",
          "external-stop fas fa-video-slash tooltip"
        );
        const stopVideoTooltip = document.createElement("span");
        stopVideoTooltip.setAttribute("class", "tooltiptext");
        stopVideoTooltip.innerText = "" + i18n.t("tooltips.noShowCam");
        stopVideoIcon.onclick = () => {
          this.sendDataMessage("stop_video", attendeeId);
        };
        stopVideoIcon.appendChild(stopVideoTooltip);
        buzzedIcon.setAttribute("class", "external-stop fas fa-bullhorn tooltip");
        const buzzedTooltip = document.createElement("span");
        buzzedTooltip.setAttribute("class", "tooltiptext");
        buzzedTooltip.innerText = "" + i18n.t("tooltips.buzz");
        buzzedIcon.onclick = () => {
          this.sendDataMessage("buzzed_user", attendeeId);
          buzzedIcon.style.pointerEvents = "none";
          buzzedIcon.setAttribute("disabled", "");
          setTimeout(() => {
            buzzedIcon.style.pointerEvents = "all";
            buzzedIcon.removeAttribute("disabled");
          }, 5000);
        };
        buzzedIcon.appendChild(buzzedTooltip);
        /* newParticipantContainer.appendChild(infoIcon); */
        videoActionsContainer.appendChild(muteIcon);
        videoActionsContainer.appendChild(stopVideoIcon);
        videoActionsContainer.appendChild(buzzedIcon);
        newParticipantContainer.appendChild(videoActionsContainer);
      }
    }
    else {
      if (videoActionsContainer)
        videoActionsContainer.style.width = "10px";
    }
    if (meetingParticipants)
      meetingParticipants.appendChild(newParticipantContainer);
    const videoElement = document.getElementById(`video-${attendeeId}`);
    if (tileState.boundVideoElement !== null) {
      await meetingSession.audioVideo.bindVideoElement(
        tileState.tileId,
        videoElement
      );
    }
  }

  /**
   * Call observer when volume change
   * (mute or unmute) and realtime attendee speaker
   * */
  private async subscribeToAttendeeVolumeChange(
    meetingSession: DefaultMeetingSession
  ): Promise<void> {
    const { AttendeeId } = this.$store.getters["MeetingStore/getAttendee"];
    meetingSession?.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      (presentAttendeeId, present) => {
        if (!present) return;
        meetingSession?.audioVideo.realtimeSubscribeToVolumeIndicator(
          presentAttendeeId,
          (attendeeId, volume, muted, signalStrength) => {
            setTimeout(() => {
              const baseAttendeeId = new DefaultModality(attendeeId).base();
              if (baseAttendeeId !== attendeeId) return;
              if (muted === null) return;
              let containerParticipant: HTMLElement | null = null;
              if (AttendeeId === attendeeId) {
                const meetingType =
                  this.$store.getters["MeetingStore/getMeetingType"];
                if (
                  meetingType !== MEETING_TYPE.WEBINAR ||
                  this.isWebinarUser() ||
                  this.isMultiWebinarUser()
                ) {
                  containerParticipant =
                    document.getElementById("local-participant");
                }
              } else {
                containerParticipant = document.getElementById(
                  `chime-participant-${attendeeId}`
                );
              }
              if (!containerParticipant) return;
              const checkAudioIcon = document.getElementById(
                `mute-icon-${attendeeId}`
              );
              const audioIcon = checkAudioIcon || document.createElement("i");
              audioIcon.setAttribute("id", `mute-icon-${attendeeId}`);
              audioIcon.setAttribute(
                "class",
                muted ? "mute-icon fas fa-microphone-slash" : ""
              );
              containerParticipant.appendChild(audioIcon);
            }, 1000);
          }
        );
      }
    );
    const activeSpeakerCallback = (attendeeIds) => {
      if (attendeeIds.length > 0) {
        // eslint-disable-next-line array-callback-return
        attendeeIds.map((el, index) => {
          const videoElement =
            AttendeeId !== el
              ? document.getElementById(`video-${el}`)
              : document.getElementById("my-video-element");
          if (!videoElement) return;
          videoElement.setAttribute(
            "class",
            index === 0 ? "video-element speak" : "video-element no-speak"
          );
        });
      }
    };
    meetingSession.audioVideo.subscribeToActiveSpeakerDetector(
      new DefaultActiveSpeakerPolicy(),
      activeSpeakerCallback
    );
  }

  /**
   * Remove attendee container when this exit of meeting
   * */
  async removeOrAddAttendee(
    meetingSession: DefaultMeetingSession
  ): Promise<void> {
    if (!meetingSession) return;
    const { AttendeeId } = this.$store.getters["MeetingStore/getAttendee"];
    const attendeePresenceSet = new Set();
    meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      async (attendeeId, present, externalUserId) => {
        if (!present) {
          const activeParticipant =
            this.$store.getters["MeetingStore/getActiveParticipant"];
          if (
            activeParticipant &&
            activeParticipant.id === `chime-participant-${attendeeId}`
          ) {
            const expandedParticipantContainer = document.getElementById(
              "expanded-participant"
            );
            expandedParticipantContainer.style.height = "0px";
            activeParticipant["style"].width = "208px";
            await this.$store.dispatch(
              "MeetingStore/setActiveParticipantAction",
              null
            );
            this.onDisplayModeChange();
            this.sortUsers(this.$store);
          }
          attendeePresenceSet.delete(externalUserId);
          const meetingType =
            this.$store.getters["MeetingStore/getMeetingType"];
          const space = this.$store.getters["SpaceStore/getSpace"];
          if (meetingType !== MEETING_TYPE.WEBINAR)
            await this.$store.dispatch(
              "MeetingStore/removeUsersInCallAction",
              attendeeId
            );
          const participantContainer = document.getElementById(
            `chime-participant-${attendeeId}`
          );
          if (!participantContainer) return;
          participantContainer.remove();
          if (!attendeeId.includes("content")) {
            let countPlayerInCall = parseInt(this.$store.getters["MeetingStore/getCountPlayerInCall"]);
            this.$store?.dispatch("MeetingStore/setCountPlayerInCallAction", (countPlayerInCall - 1));
          }
          this.onDisplayModeChange();
        } else {
          await attendeePresenceSet.add(externalUserId);
          if (AttendeeId !== attendeeId) {
            const t = meetingSession.audioVideo.getAllRemoteVideoTiles();
            const searchT = t
              .map((a) => a.state())
              .filter((a) => a.boundAttendeeId === attendeeId);
            let tileState = null;
            if (searchT.length <= 0) {
              const videoTile = meetingSession.audioVideo.addVideoTile();
              tileState = videoTile.state();
              tileState.boundAttendeeId = attendeeId;
              tileState.boundExternalUserId = externalUserId;
              tileState.active = true;
            } else {
              tileState = searchT[0];
            }
            const meetingType =
              this.$store.getters["MeetingStore/getMeetingType"];
            const space = this.$store.getters["SpaceStore/getSpace"];
            // Commented because what it does?
            if (meetingType !== MEETING_TYPE.WEBINAR)
              this.addUsersInCall(tileState, attendeeId);
            const splitData = tileState.boundExternalUserId.split("#");
            const indexWebinarHost = splitData.findIndex((a) =>
              a.includes("isWebinarHost")
            );
            const isWebinarHost =
              indexWebinarHost >= 0
                ? splitData[indexWebinarHost].split("-")[1] === "1"
                : false;
            if (
              (meetingType === MEETING_TYPE.WEBINAR && isWebinarHost) ||
              meetingType !== MEETING_TYPE.WEBINAR
            ) {
              const user = this.searchAttendeeUser(tileState);
              await this.addOtherMeetingUser(
                meetingSession,
                tileState,
                attendeeId,
                this.searchAttendeeName(tileState),
                user && user.user.profileImage
                  ? user.user.profileImage.url
                  : // eslint-disable-next-line @typescript-eslint/no-var-requires
                  require("@/assets/images/logo/favicon.png")
              );
              await this.expandVideoOnClick(`chime-participant-${attendeeId}`);
              await this.simulateClickInShareScreen(
                `chime-participant-${attendeeId}`
              );
              this.onDisplayModeChange();
            }
          }
        }
      }
    );
  }

  /**
   * Call when click on enable or disable video
   * */
  async startOrStopLocalVideo(status: boolean): Promise<void> {
    setTimeout(async () => {
      const meetingSession =
        this.$store.getters["MeetingStore/getMeetingSession"];
      if (!meetingSession)
        return;
      if (status) {
        let transformDevice: DefaultVideoTransformDevice =
          this.$store.getters["MeetingStore/getTransformDevice"];
        if (transformDevice) {
          await meetingSession?.audioVideo.startVideoInput(transformDevice);
        }
        await meetingSession.audioVideo.startLocalVideoTile();
      } else {
        await meetingSession.audioVideo.stopVideoInput();
        await meetingSession.audioVideo.stopLocalVideoTile();
        await meetingSession.audioVideo.removeLocalVideoTile();
      }
    }, 500);
  }
  /**
   * Call when click on enable or disable mic
   * */
  async muteOrUnmuteAudioInput(status: boolean): Promise<void> {
    const meetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    if (!meetingSession) return;
    if (status) {
      meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      this.setMeetingAudioInputDevice(meetingSession, this.$store);
      return;
    }
    meetingSession.audioVideo.realtimeMuteLocalAudio();
    // TODO If it doesn't sound bad when muted
    //meetingSession.audioVideo.startAudioInput(null);
  }

  /**
   * Call to lower the quality of the video
   * */
  async lowerVideoQuality(): Promise<void> {
    const camEnabled = this.$store.getters["MeetingStore/getCameraEnabled"];
    const meetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    if (!meetingSession) return;
    //await meetingSession.audioVideo.chooseVideoInputQuality(this.QUALITY_LOW.width, this.QUALITY_LOW.height, this.QUALITY_LOW.frameRate);
    //await meetingSession.audioVideo.setVideoMaxBandwidthKbps(this.QUALITY_LOW.kbps);
    await this.startOrStopLocalVideo(camEnabled);
  }

  /**
   * Call when click on share screen
   * */
  async shareScreen(status: boolean): Promise<void> {
    try {
      const meetingSession: DefaultMeetingSession =
        this.$store.getters["MeetingStore/getMeetingSession"];
      if (!meetingSession) return;
      if (status) {
        // const browserBehavior = new DefaultBrowserBehavior();
        // if (browserBehavior.isSimulcastSupported()) {
        //   await meetingSession.audioVideo.enableSimulcastForContentShare(true);
        // }
        await meetingSession?.audioVideo.startContentShareFromScreenCapture(
          null,
          this.CURRENT_QUALITY.shareFrameRate
        );
        return;
      }
      meetingSession?.audioVideo.stopContentShare();
    } catch (error) {
      switch (error["message"]) {
        case "Permission denied by system": {
          if (localStorage.getItem("OSName") == "Mac/iOS") {
            window.open(
              "x-apple.systempreferences:com.apple.preference.security?Privacy_ScreenCapture"
            );
          }
          break;
        }
      }
    }
  }

  /**
   * Call when display mode change or new attendee join
   * */
  onDisplayModeChange(): void {
    const columnDisplay = this.$store.getters["MeetingStore/getColumnDisplay"];
    const callContentContainer = document.getElementById(
      "call-content-container"
    );
    const meetingParticipants: HTMLElement | null = document.getElementById(
      "meeting-participants"
    );
    const expandedParticipantContainer = document.getElementById(
      "expanded-participant"
    );

    if (
      !meetingParticipants ||
      !callContentContainer ||
      !expandedParticipantContainer
    ) {
      return;
    }

    const numParticipants = meetingParticipants.childElementCount;
    const children = meetingParticipants.children;

    const activeParticipant =
      this.$store.getters["MeetingStore/getActiveParticipant"];
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    if (
      columnDisplay ||
      activeParticipant ||
      meetingType === MEETING_TYPE.SPEED_NETWORKING
    ) {
      for (let i = 0; i < numParticipants; i++) {
        const child = children[i];
        if (child.id === "local-participant") {
          this.displayLocalName();
          const space = this.$store.getters["SpaceStore/getSpace"];
          if (
            (meetingType === MEETING_TYPE.WEBINAR &&
              (space.superSpace.isMultiWebinarActivated
                ? this.isMultiWebinarUser()
                : this.isWebinarUser())) ||
            meetingType !== MEETING_TYPE.WEBINAR
          ) {
            child["style"] = "";
            let video = null;
            if (
              child &&
              child.getElementsByTagName &&
              child.getElementsByTagName("video").length > 0
            )
              video = child.getElementsByTagName("video")[0];
            if (!video) break;
            video.style.width = "";
          }
        } else {
          child["style"] = "";
          let video = null;
          if (
            child &&
            child.getElementsByTagName &&
            child.getElementsByTagName("video").length > 0
          )
            video = child.getElementsByTagName("video")[0];
          if (!video) break;
          video.style.width = "";
        }
      }
      if (!columnDisplay && activeParticipant) {
        if (!this.isTopShare()) {
          expandedParticipantContainer.style.height =
          expandedParticipantContainer.offsetWidth * 0.4 + "px";
          activeParticipant.style.height = "100%";
        }
      } else if (columnDisplay && activeParticipant) {
        activeParticipant.style.height = "";
      }
      meetingParticipants.style.height = "unset";
      setTimeout(async () => {
        await this.$store.dispatch(
          "GraphicClientStore/resizeGraphicClientAction"
        );
      }, 20);
      return;
    }

    const margin = 10;
    setTimeout(() => {
      let width =
        meetingParticipants.offsetWidth -
        margin * (numParticipants >= 5 ? 10 : numParticipants * 2);
      width = width / (numParticipants >= 5 ? 5 : numParticipants);

      this.setWidth(width, margin, children, numParticipants);
    }, 50);
  }
  /**
   * Set width to video content element
   * */
  private setWidth(width, margin, children, numParticipants): void {
    for (let i = 0; i < numParticipants; i++) {
      const child = children[i];
      if (!child) {
        let video = null;
        if (
          child?.getElementsByTagName &&
          child?.getElementsByTagName("video").length > 0
        )
          video = child.getElementsByTagName("video")[0];
        if (!video) break;
        video.style.width = "100%";
        child["style"].margin = margin + "px";
        child["style"].width = width >= 220 ? width + "px" : "220px";
        child["style"].height =
          width >= 220 ? width * 0.56 + "px" : 220 * 0.56 + "px";
      }
    }
  }

  /**
   * this is thest to show metrics connections
   * */
  private connectionsMetrics(meetingSession: DefaultMeetingSession) {
    const observer = {
      oldSendBandwidthKbs: 0,
      oldRecvBandwidthKbs: 0,
      metricsDidReceive: (clientMetricReport) => {
        const metricReport = clientMetricReport.getObservableMetrics();
        const {
          videoPacketSentPerSecond,
          videoUpstreamBitrate,
          nackCountPerSecond,
        } = metricReport;
        const availableSendBandwidthKbs = metricReport.availableOutgoingBitrate / 1000;
        const availableRecvBandwidthKbs = metricReport.availableIncomingBitrate / 1000;

        // videoSendHealthDidChange
        //console.log(`Sending video bitrate in kilobits per second: ${videoUpstreamBitrate / 1000} and sending packets per second: ${videoPacketSentPerSecond}`);

        // videoSendBandwidthDidChange
        if (this.oldSendBandwidthKbs != availableSendBandwidthKbs) {
          //console.log(`Sending bandwidth is ${availableSendBandwidthKbs}, nack count per second is ${nackCountPerSecond}, and old bandwidth is ${this.oldSendBandwidthKbs}`);
          this.oldSendBandwidthKbs = availableSendBandwidthKbs;
        }

        // videoReceiveBandwidthDidChange
        if (this.oldRecvBandwidthKbs != availableRecvBandwidthKbs) {
          //console.log(`Receiving bandwidth is ${availableRecvBandwidthKbs}, and old bandwidth is ${this.oldRecvBandwidthKbs}`);
          this.oldRecvBandwidthKbs = availableRecvBandwidthKbs;
        }
      },
      connectionDidBecomeGood: () => {
        if (process.env.VUE_APP_ENVIRONMENT != "PRO")
          console.log("CHIME connectionDidBecomeGood Your connection is good!");
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        this.sendDataMessage("good_connection", attendee.AttendeeId);
      },
      connectionDidBecomePoor: () => {
        if (process.env.VUE_APP_ENVIRONMENT != "PRO")
          console.log("CHIME connectionDidBecomePoor Your connection is poor");
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        this.sendDataMessage("low_connection", attendee.AttendeeId);
      },
      connectionDidSuggestStopVideo: () => {
        this.$vs.notification({
          sticky: true,
          buttonClose: true,
          duration: 6000,
          position: "bottom-center",
          color: "danger",
          text: `${i18n.t("notifications.descriptions.recommendTurningOff")}`,
        });
        if (process.env.VUE_APP_ENVIRONMENT != "PRO")
          console.log("CHIME connectionDidSuggestStopVideo Recommend turning off your video");
      },
      videoSendDidBecomeUnavailable: () => {
        // Chime SDK allows a total of 16 simultaneous videos per meeting.
        // If you try to share more video, this method will be called.
        // See videoAvailabilityDidChange below to find out when it becomes available.
        // console.log("You cannot share your video");
      },
      videoAvailabilityDidChange: (videoAvailability) => {
        // canStartLocalVideo will also be true if you are already sharing your video.
        if (videoAvailability.canStartLocalVideo) {
          if (process.env.VUE_APP_ENVIRONMENT != "PRO")
            console.log("CHIME videoAvailabilityDidChange You can share your video");
        } else {
          if (process.env.VUE_APP_ENVIRONMENT != "PRO")
            console.log("CHIME videoAvailabilityDidChange You cannot share your video");
        }
      },
      estimatedDownlinkBandwidthLessThanRequired: (
        estimatedBandwidth: number,
        requiredBandwidth: number
      ) => {
        /*this.$vs.notification({
          sticky: true,
          buttonClose: true,
          duration: 6000,
          position: "bottom-center",
          color: "danger",
          text: `${i18n.t("notifications.descriptions.recommendTurningOff")}`,
        });*/
        if (process.env.VUE_APP_ENVIRONMENT != "PRO")
          console.log(
            `CHIME estimatedDownlinkBandwidthLessThanRequired estimated Bandwidth ${estimatedBandwidth}, required Bandwidth ${requiredBandwidth}`
          );
      },
      videoNotReceivingEnoughData() {
        if (process.env.VUE_APP_ENVIRONMENT != "PRO")
          console.log("CHIME videoNotReceivingEnoughData List of Attendee and bandwitch");
        // TODO: Desctivar attendee video
        /*for (const clientVideoStreamReceivingReport of receivingDataMap) {
          meetingSession?.audioVideo.pauseVideoTile(
            clientVideoStreamReceivingReport.attendeeId
          );
        }*/
        //
      },
    };

    meetingSession?.audioVideo.addObserver(observer);
  }

  /**
   * Set the local user name in the span title name
   * */
  private displayLocalName(): void {
    const localSpaceUser = this.$store.getters["UserStore/getSpaceUser"];
    const spanName = document.getElementById("local-attendee-title");
    if (spanName)
      spanName.innerText = `${localSpaceUser.superSpaceUser.user.name} ${localSpaceUser.superSpaceUser.user.lastName
        ? localSpaceUser.superSpaceUser.user.lastName
        : ""
        }`;
  }

  /**
   * Return the username of attendee
   * */
  private searchAttendeeName(tileState: any): string {
    const tileStateSplit = tileState.boundExternalUserId.split("#");
    const indexName = tileStateSplit.findIndex((a) => a.includes("name"));
    const id = parseInt(tileStateSplit[0].split("-")[1]);
    const name = tileStateSplit[indexName].split("-")[1].replaceAll("@", " ");
    const userSpaces = this.$store.getters["SpaceStore/getUsersInSpace"];
    const spaceUser = userSpaces.filter((a) => a.user.id === id)[0];
    if (!spaceUser) return name;
    return !tileState.boundAttendeeId.includes("#content")
      ? `${spaceUser.user.name} ${spaceUser.user.lastName ? spaceUser.user.lastName : ""
      } `
      : `${spaceUser.user.name} ${i18n.t("labels.sharing")}`;
  }

  private searchAttendeeUser(tileState: any) {
    const id = parseInt(
      tileState.boundExternalUserId.split("#")[0].split("-")[1],
      10
    );
    const userSpaces = this.$store.getters["SpaceStore/getUsersInSpace"];
    return userSpaces.filter((a) => a.user.id === id)[0];
  }

  /**
   * Check in devicelist if device is available
   * */
  checkDeviceIsAvailable(deviceId: string, deviceList: any): boolean {
    if (!deviceList || deviceList.length <= 0) return true;
    return deviceList.some((a) => a.deviceId === deviceId);
  }

  private simulateClickInShareScreen(participantId) {
    if (participantId.includes("#content")) {
      const participantContainer = document.getElementById(participantId);
      setTimeout(() => {
        if (
          participantContainer &&
          participantContainer.getElementsByTagName &&
          participantContainer.getElementsByTagName("video").length > 0
        )
          participantContainer.getElementsByTagName("video")[0].click();
      }, 500);
    }
  }

  private expandVideoOnClick(participantId: string) {
    const participantContainer = document.getElementById(participantId);
    const meetingParticipants = document.getElementById("meeting-participants");
    const expandedParticipantContainer = document.getElementById(
      "expanded-participant"
    );
    if (
      !participantContainer ||
      !expandedParticipantContainer ||
      !meetingParticipants
    )
      return;
    if (
      participantContainer &&
      participantContainer.getElementsByTagName &&
      participantContainer.getElementsByTagName("video").length > 0
    )
      participantContainer.getElementsByTagName("video")[0].onclick =
        async () => {
          const activeParticipant =
            this.$store.getters["MeetingStore/getActiveParticipant"];

          if (activeParticipant) {
            expandedParticipantContainer.style.height =
              expandedParticipantContainer.offsetWidth * 0.36 + "px";
            expandedParticipantContainer.innerHTML = "";
            if (activeParticipant.id === "local-participant") {
              meetingParticipants.prepend(activeParticipant);
            } else {
              meetingParticipants.append(activeParticipant);
            }
            if (activeParticipant.id === participantId) {
              expandedParticipantContainer.style.height = "0px";
              expandedParticipantContainer.style.width = "0rem";
              expandedParticipantContainer.style.maxWidth = "0px";
              activeParticipant["style"].width = "208px";
              activeParticipant["style"].height = "";
              const videoChange =
                activeParticipant.getElementsByTagName("video")[0];
              if (videoChange) videoChange.style.width = "208px";
              await this.$store.dispatch(
                "MeetingStore/setActiveParticipantAction",
                null
              );
              this.onDisplayModeChange();
              return;
            }
            this.onDisplayModeChange();
          }

          await this.$store.dispatch(
            "MeetingStore/setActiveParticipantAction",
            participantContainer
          );
          const videoExpanded =
            expandedParticipantContainer.getElementsByClassName(
              "video-container"
            );

          expandedParticipantContainer.appendChild(participantContainer);
          await this.$store.dispatch(
            "MeetingStore/setColumnDisplayAction",
            false
          );
          /*  const layoutType = this.$store.getters["MeetingStore/getLayoutType"];
          if (layoutType === LAYOUT_TYPE.COLUMN)
            await this.$store.dispatch(
              "MeetingStore/setLayoutTypeAction",
              LAYOUT_TYPE.GRID
            );
          await this.$store.dispatch(
            "SidebarStore/setSidebarFullAction",
            false
          ); */
          expandedParticipantContainer.style.width = "60rem";
          expandedParticipantContainer.style.maxWidth = "100%";
          expandedParticipantContainer.style.height = "70%";
          let participant = null;
          let video = null;
          if (
            expandedParticipantContainer &&
            expandedParticipantContainer.getElementsByTagName &&
            expandedParticipantContainer.getElementsByTagName("div").length > 0
          )
            participant =
              expandedParticipantContainer.getElementsByTagName("div")[0];
          if (
            expandedParticipantContainer &&
            expandedParticipantContainer.getElementsByTagName &&
            expandedParticipantContainer.getElementsByTagName("video").length >
            0
          )
            video =
              expandedParticipantContainer.getElementsByTagName("video")[0];
          if (participant) {
            participant.style.height = "100%";
            participant.style.width = "";
          }
          if (video) video.style.width = "100%";
          this.onDisplayModeChange();
          this.sortUsers(this.$store);
          if (participantId.includes("#content")) {
            expandedParticipantContainer.style.height = "30rem";
          }
        };
  }

  async addBackgroundImage(backgroundImage) {
    const blurEnabled = this.$store.getters["MeetingStore/getBlurEnabled"];
    if (blurEnabled) await this.stopBlur();
    const logger = new ConsoleLogger(
      "CHIME_BLUR_LOGS addBackgroundImage ",
      process.env.VUE_APP_ENVIRONMENT === "PRO" ? LogLevel.OFF : LogLevel.ERROR
    );

    await this.$store.dispatch(
      "MeetingStore/setBackgroundProcessorsAction",
      []
    );

    const processors =
      this.$store.getters["MeetingStore/getBackgroundProcessors"];

    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    let transformDevice: DefaultVideoTransformDevice =
      this.$store.getters["MeetingStore/getTransformDevice"];
    const image = await fetch(backgroundImage);
    const imageBlob = await image.blob();
    /*if (
      processors.find((e) => e.filterType === "background replacement") &&
      transformDevice
    ) {
      const processorBackground = processors.find(
        (e) => e.filterType === "background replacement"
      );
      if (processorBackground) {
       processorBackground.setImageBlob(imageBlob);
      }
      await this.$store.dispatch(
        "MeetingStore/setBackgroundProcessorsAction",
        processors
      );
    } else {*/
    const options = { imageBlob };
    const replacementProcessor =
      await BackgroundReplacementVideoFrameProcessor.create(null, options);
    processors.push(replacementProcessor);
    const videoInputId =
      this.$store.getters["MeetingStore/getSelectedVideoInput"];
    const videoInputDevices =
      await meetingSession?.audioVideo.listVideoInputDevices(true);
    const deviceId = this.checkDeviceIsAvailable(
      videoInputId,
      videoInputDevices
    )
      ? videoInputId
      : videoInputDevices[0].deviceId;
    transformDevice = new DefaultVideoTransformDevice(
      logger,
      deviceId?.toString(),
      processors
    );
    await this.$store.dispatch(
      "MeetingStore/setTransformDeviceAction",
      transformDevice
    );
    //}
    meetingSession?.audioVideo.startVideoInput(transformDevice);

    await this.$store.dispatch(
      "MeetingStore/setBackgroundImageAction",
      backgroundImage
    );
    await this.$store.dispatch("MeetingStore/setBackgroundEnabled", true);
  }

  async stopBackground() {
    const processors =
      this.$store.getters["MeetingStore/getBackgroundProcessors"];
    const storeTransformDevice: DefaultVideoTransformDevice =
      this.$store.getters["MeetingStore/getTransformDevice"];
    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    await processors[0]?.destroy();
    await storeTransformDevice?.stop();
    await this.$store.dispatch(
      "MeetingStore/setBackgroundProcessorsAction",
      []
    );
    await this.$store.dispatch("MeetingStore/setTransformDeviceAction", null);
    await this.$store.dispatch("MeetingStore/setBackgroundImageAction", "");
    await this.$store.dispatch("MeetingStore/setBackgroundEnabled", false);
    await this.setMeetingVideoInputDevice(meetingSession, this.$store);
  }

  async addBackgroundBlur(blurAmount) {
    await this.$store.dispatch("MeetingStore/setBlurProcessorsAction", []);
    const backgroundEnabled =
      this.$store.getters["MeetingStore/getBackgroundEnabled"];
    if (backgroundEnabled) await this.stopBackground();
    const logger = new ConsoleLogger(
      "CHIME_BLUR_LOGS addBackgroundBlur ",
      process.env.VUE_APP_ENVIRONMENT === "PRO" ? LogLevel.OFF : LogLevel.ERROR
    );
    const processors = this.$store.getters["MeetingStore/getBlurProcessors"];
    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    let transformDevice: DefaultVideoTransformDevice =
      this.$store.getters["MeetingStore/getTransformDevice"];
    if (processors.length > 0 && transformDevice) {
      processors[0].setBlurStrength(blurAmount);
      await this.$store.dispatch(
        "MeetingStore/setBlurProcessorsAction",
        processors
      );
    } else {
      const blurOptions: BackgroundBlurOptions = {
        logger,
        reportingPeriodMillis: 2000,
        blurStrength: 1,
      };
      const blurProcessor = await BackgroundBlurVideoFrameProcessor.create(
        null,
        blurOptions
      );
      blurProcessor.setBlurStrength(blurAmount);
      processors.push(blurProcessor);
      await this.$store.dispatch(
        "MeetingStore/setBlurProcessorsAction",
        processors
      );
      /* Busca el id del dispositivo */
      const videoInputId =
        this.$store.getters["MeetingStore/getSelectedVideoInput"];
      const videoInputDevices =
        await meetingSession?.audioVideo.listVideoInputDevices(true);
      const deviceId = this.checkDeviceIsAvailable(
        videoInputId,
        videoInputDevices
      )
        ? videoInputId
        : videoInputDevices[0].deviceId;
      // add the processor to the pipeline
      transformDevice = new DefaultVideoTransformDevice(
        logger,
        deviceId?.toString(),
        processors
      );
      await this.$store.dispatch(
        "MeetingStore/setTransformDeviceAction",
        transformDevice
      );
    }
    meetingSession.audioVideo.chooseVideoInputQuality(
      this.CURRENT_QUALITY.width,
      this.CURRENT_QUALITY.height,
      this.CURRENT_QUALITY.frameRate
    );
    meetingSession.audioVideo.setVideoMaxBandwidthKbps(this.QUALITY_LOW.kbps);
    meetingSession?.audioVideo.startVideoInput(transformDevice);

    await this.$store.dispatch("MeetingStore/setBlurAmountAction", blurAmount);
    await this.$store.dispatch("MeetingStore/setBlurEnabled", true);
  }
  async stopBlur() {
    const processors = this.$store.getters["MeetingStore/getBlurProcessors"];
    const storeTransformDevice: DefaultVideoTransformDevice =
      this.$store.getters["MeetingStore/getTransformDevice"];
    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    await processors[0]?.destroy();
    await storeTransformDevice?.stop();
    await this.$store.dispatch("MeetingStore/setBlurProcessorsAction", []);
    await this.$store.dispatch("MeetingStore/setTransformDeviceAction", null);
    await this.$store.dispatch("MeetingStore/setBlurAmountAction", 0);
    await this.$store.dispatch("MeetingStore/setBlurEnabled", false);
    await this.setMeetingVideoInputDevice(meetingSession, this.$store);
  }

  async addVoiceFocus() {
    try {
      /*buscamos el id del dispositivo de entrada*/
      const meetingSession: DefaultMeetingSession =
        this.$store.getters["MeetingStore/getMeetingSession"];
      const audioInputDevices =
        await meetingSession?.audioVideo.listAudioInputDevices();
      const audioInputId =
        this.$store.getters["MeetingStore/getSelectedAudioInput"];
      const deviceId = this.checkDeviceIsAvailable(
        audioInputId,
        audioInputDevices
      )
        ? audioInputId
        : audioInputDevices[0].deviceId;
      const spec: VoiceFocusSpec = {
        variant: "c50",
        executionPreference: "inline",
        simd: "force",
      };
      const voiceFocusDeviceTransformer =
        await VoiceFocusDeviceTransformer.create(spec);
      const transformDevice =
        await voiceFocusDeviceTransformer.createTransformDevice(deviceId);
      meetingSession?.audioVideo.startAudioInput(transformDevice);
      await this.$store.dispatch(
        "MeetingStore/setVoiceTransformDevice",
        transformDevice
      );
      await this.$store.dispatch("MeetingStore/setVoiceIsEnabledAction", true);
    } catch (e) {
      console.log(e);
    }
  }
  async stopVoiceFocus() {
    const transformDevice = await this.$store.getters[
      "MeetingStore/getVoiceTransformDevice"
    ];
    if (transformDevice) transformDevice.stop();
    await this.$store.dispatch("MeetingStore/setVoiceIsEnabledAction", false);
    await this.$store.dispatch("MeetingStore/setVoiceTransformDevice", null);
    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    await this.setMeetingAudioInputDevice(meetingSession, this.$store);
  }
  async searchExternalUser(userId) {
    const id = userId.split("-")[1];
    const userSpaces = this.$store.getters["SpaceStore/getUsersInSpace"];
    const superSpaceUser = userSpaces.filter(
      (a) => a.user.id === parseInt(id)
    )[0];
    return `${superSpaceUser.user.name} ${superSpaceUser.user.lastName ? superSpaceUser.user.lastName : ""
      } `;
  }
  async sendDataMessage(topic, attendeId) {
    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    meetingSession.audioVideo.realtimeSendDataMessage(topic, attendeId);
  }
  async receiveDataMessage() {
    const meetingSession: DefaultMeetingSession =
      this.$store.getters["MeetingStore/getMeetingSession"];
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "low_connection",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        let containerParticipant = null;
        if (attendee.AttendeeId !== text) {
          containerParticipant = document.getElementById(
            `chime-participant-${text}`
          );
        }
        if (containerParticipant) {
          const checkHandIcon = document.getElementById(
            `low-connection-${text}`
          );

          const lowConnection = checkHandIcon || document.createElement("span");
          lowConnection.innerText = "" + i18n.t("labels.lowBandWidth");
          lowConnection.setAttribute("id", `low-connection-${text}`);
          lowConnection.setAttribute("class", "title-low-connection");
          containerParticipant.appendChild(lowConnection);

          //await this.lowerVideoQuality();

          //Todo icono antiguo de low_connection
          /*const lowIcon = checkHandIcon || document.createElement("i");
          lowIcon.setAttribute("id", `low-connection-${text}`);
          lowIcon.setAttribute("class", "low-icon fas fa-wifi");
          containerParticipant.appendChild(lowIcon);*/
        }
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "good_connection",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        let containerParticipant = null;
        if (attendee.AttendeeId !== text) {
          containerParticipant = document.getElementById(
            `chime-participant-${text}`
          );
        }
        if (containerParticipant) {
          const checkHandIcon = document.getElementById(
            `low-connection-${text}`
          );

          const lowConnection = checkHandIcon || document.createElement("span");
          lowConnection.innerText = "";
          lowConnection.setAttribute("id", `low-connection-${text}`);
          lowConnection.setAttribute("class", "");
          containerParticipant.appendChild(lowConnection);

          //Todo icono antiguo de good_connection
          /*const lowIcon = checkHandIcon || document.createElement("i");
          lowIcon.setAttribute("id", `low-connection-${text}`);
          lowIcon.setAttribute("class", "");
          containerParticipant.appendChild(lowIcon);*/
        }
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "hand_up_up",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        let containerParticipant = null;
        if (attendee.AttendeeId !== text) {
          containerParticipant = document.getElementById(
            `chime-participant-${text}`
          );
        }
        if (containerParticipant) {
          const checkHandIcon = document.getElementById(`hand-up-${text}`);
          const handIcon = checkHandIcon || document.createElement("i");
          handIcon.setAttribute("id", `hand-up-${text}`);
          handIcon.setAttribute("class", "hand-up fas fa-hand-paper");
          containerParticipant.appendChild(handIcon);
        }
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "hand_up_down",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        let containerParticipant = null;
        if (attendee.AttendeeId !== text) {
          containerParticipant = document.getElementById(
            `chime-participant-${text}`
          );
        }
        if (containerParticipant) {
          const checkHandIcon = document.getElementById(`hand-up-${text}`);
          const handIcon = checkHandIcon || document.createElement("i");
          handIcon.setAttribute("id", `hand-up-${text}`);
          handIcon.setAttribute("class", "");
          containerParticipant.appendChild(handIcon);
        }
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "user_connected",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        //const attendee = this.$store.getters["MeetingStore/getAttendee"];
        //if (attendee.AttendeeId !== text) {
        const audio = new Audio(require("@/assets/audio/user_joinedroom.mp3"));
        audio.play().then();

        /*const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    if (meetingType && meetingType === MEETING_TYPE.MEETING) {
      if (!this.soundUser.includes(attendeeId)) {
        if (!attendeeId.includes("content")) {
          this.soundUser.push(attendeeId);
          await this.sendDataMessage("user_connected", attendeeId);
        }
      }
    }*/
        //}
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "record_start",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const namePath = dataMessage.senderExternalUserId.split("#");
        const labelName = namePath.find(element => element.includes('name'));
        const name = labelName.split("-");
        const finalName = name[1].replaceAll("@", " ");
        await this.$store.dispatch("MeetingStore/setCallIsRecordAction", true);
        this.$vs.notification({
          sticky: true,
          buttonClose: true,
          duration: 4000,
          position: "bottom-center",
          color: "primary",
          text: `${i18n.t("recordingCall", { name: finalName })}`,
        });
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "record_stop",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);

        await this.$store.dispatch("MeetingStore/setCallIsRecordAction", false);
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "stop_audio",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        if (attendee.AttendeeId === text) {
          await this.muteOrUnmuteAudioInput(false);
          await this.$store.dispatch("MeetingStore/setMicEnabledAction", false);
          const userName = await this.searchExternalUser(
            dataMessage.senderExternalUserId
          );
          if (!this.muteNotification)
            this.muteNotification = this.$vs.notification({
              sticky: true,
              buttonClose: true,
              duration: "none",
              position: "bottom-center",
              color: "primary",
              text: `${userName} ${i18n.t(
                "notifications.descriptions.muteExternalMic"
              )}`,
              onDestroy: () => {
                this.muteNotification = null;
              },
            });
        }
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "buzzed_user",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        if (attendee.AttendeeId === text) {
          const mainContainer = document.getElementById("main-container");
          mainContainer.className = "main-container animate-buzzed";
          const micEnabled = this.$store.getters["MeetingStore/getMicEnabled"];
          const config: ConfigInterface =
            this.$store.getters["UserStore/getConfig"];
          if (!micEnabled && !config.notification.silenceNotification) {
            const buzzed = new Audio(require("@/assets/audio/buzzed.mp3"));
            buzzed.play();
          }
          setTimeout(() => {
            mainContainer.className = "main-container";
          }, 1000);
          const userName = await this.searchExternalUser(
            dataMessage.senderExternalUserId
          );
          if (!this.buzzedNotification)
            this.buzzedNotification = this.$vs.notification({
              sticky: true,
              buttonClose: true,
              duration: "none",
              position: "bottom-center",
              color: "primary",
              text: `${userName} ${i18n.t(
                "notifications.descriptions.buzzed"
              )}`,
              onDestroy: () => {
                this.buzzedNotification = null;
              },
            });
        }
      }
    );
    meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(
      "stop_video",
      async (dataMessage) => {
        const text = new TextDecoder().decode(dataMessage.data);
        const attendee = this.$store.getters["MeetingStore/getAttendee"];
        if (attendee.AttendeeId === text) {
          await this.startOrStopLocalVideo(false);
          await this.$store.dispatch(
            "MeetingStore/setCameraEnabledAction",
            false
          );
          const userName = await this.searchExternalUser(
            dataMessage.senderExternalUserId
          );
          if (!this.stopNotification)
            this.stopNotification = this.$vs.notification({
              sticky: true,
              buttonClose: true,
              duration: "none",
              position: "bottom-center",
              color: "primary",
              text: `${userName} ${i18n.t(
                "notifications.descriptions.stopExternalCam"
              )}`,
              onDestroy: () => {
                this.stopNotification = null;
              },
            });
        }
      }
    );
  }
  async muteAllMics() {
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    const user = this.$store.getters["UserStore/getSpaceUser"];
    if (
      (meetingType === MEETING_TYPE.WEBINAR && user.isWebinarHost) ||
      meetingType !== MEETING_TYPE.WEBINAR
    ) {
      const usersInCall = this.$store.getters["MeetingStore/getUsersInCall"];
      usersInCall.map((a) => this.sendDataMessage("stop_audio", a.attendeeId));
    }
  }
  async stopAllVideo() {
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    const user = this.$store.getters["UserStore/getSpaceUser"];
    if (
      (meetingType === MEETING_TYPE.WEBINAR && user.isWebinarHost) ||
      meetingType !== MEETING_TYPE.WEBINAR
    ) {
      const usersInCall = this.$store.getters["MeetingStore/getUsersInCall"];
      usersInCall.map((a) => this.sendDataMessage("stop_video", a.attendeeId));
    }
  }
  async sendStartRecord() {
    const user = this.$store.getters["UserStore/getSpaceUser"];
    await this.sendDataMessage("record_start", user.id);
  }
  async sendStopRecord() {
    const user = this.$store.getters["UserStore/getSpaceUser"];
    await this.sendDataMessage("record_stop", user.id);
  }
  async handUp(isUp) {
    const attendee = this.$store.getters["MeetingStore/getAttendee"];
    const containerParticipant = document.getElementById(`local-participant`);

    if (containerParticipant) {
      const checkHandIcon = document.getElementById(`hand-up-local`);
      const handIcon = checkHandIcon || document.createElement("i");
      handIcon.setAttribute("id", `hand-up-local`);
      handIcon.setAttribute(
        "class",
        !checkHandIcon || checkHandIcon.className === ""
          ? "hand-up fas fa-hand-paper"
          : ""
      );
      containerParticipant.appendChild(handIcon);
    }
    await this.sendDataMessage(
      isUp ? "hand_up_up" : "hand_up_down",
      attendee.AttendeeId
    );
  }

  async checkPermisionMicro(): Promise<boolean> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<boolean>(async function (resolve, reject) {
      const isFirefox = /^((?!chrome|android).)*firefox/i.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const mic = "microphone" as PermissionName;
      if (!isFirefox && !isSafari) {
        const microphonePermission = await navigator.permissions.query({
          name: mic,
        });
        if (
          microphonePermission.state === "denied" ||
          microphonePermission.state === "prompt"
        ) {
          resolve(false);
        }
        resolve(true);
      }
      if (isFirefox || isSafari) {
        navigator.mediaDevices
          .getUserMedia({ video: false, audio: true })
          .then(() => {
            resolve(true);
          })
          .catch(() => {
            resolve(false);
          });
      }
    });
  }
  async checkPermisionCamera(): Promise<boolean> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<boolean>(async function (resolve, reject) {
      const isFirefox = /^((?!chrome|android).)*firefox/i.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const cam = "camera" as PermissionName;
      if (!isFirefox && !isSafari) {
        if (navigator != undefined && navigator.permissions != undefined) {
          const cameraPermission = await navigator.permissions.query({
            name: cam,
          });
          if (
            cameraPermission.state === "denied" ||
            cameraPermission.state === "prompt"
          ) {
            resolve(false);
          }
          resolve(true);
        }
      }
      if (isFirefox || isSafari) {
        navigator.mediaDevices
          .getUserMedia({ video: true, audio: false })
          .then(() => resolve(true))
          .catch(async () => {
            resolve(false);
          });
      }
    });
  }

  async addUsersInCall(tileState, attendeeId) {
    /// Check Sound ///
    const meetingType = this.$store.getters["MeetingStore/getMeetingType"];
    if (meetingType && meetingType === MEETING_TYPE.MEETING) {
      const userInCall = this.$store.getters["MeetingStore/getUsersInCall"];
      let newUser = true;

      userInCall.forEach((element) => {
        if (element.attendeeId == attendeeId) {
          newUser = false;
          return;
        }
      });

      if (newUser) {
        if (!attendeeId.includes("content")) {
          let countPlayerInCall = parseInt(this.$store.getters["MeetingStore/getCountPlayerInCall"]);
          this.$store?.dispatch("MeetingStore/setCountPlayerInCallAction", (countPlayerInCall + 1));
        }
        if (this.audio.paused) {
          this.audio.play();
        }
      }
      newUser = true;
    }
    /// Fin Check Sound ///
    const jsonTileState = tileState.boundExternalUserId.split("#");
    await this.$store.dispatch("MeetingStore/addUsersInCallAction", {
      id: parseInt(jsonTileState[0].split("-")[1]),
      attendeeId: attendeeId,
    });
  }

  async sortUsers(store) {
    const participantContainer =
      store.getters["MeetingStore/getActiveParticipant"];
    const activeParticipants = document.getElementById(
      "meeting-participants"
    ).children;
    for (const participant of activeParticipants) {
      participant.setAttribute("class", "video-container");
      if (participantContainer && participantContainer.id) {
        const id = participantContainer.id.split("#")[0];
        if (participant && participant.id && participant.id === id) {
          participant.setAttribute("class", "video-container share-user");
          participant.parentNode.insertBefore(participant, activeParticipants[0]);
        }
      }
    }
  }

  isTopShare() {
    let type = localStorage.getItem("layout");
    if (type == "TOP_SHARE") return true;
    return false;
  }
}

